/**
 * A form textarea that also takes photos.
 *
 * A photo is uploaded to the incident's files and dropped into the text as a
 * marker at the cursor; the strip under the box shows each one as a thumbnail.
 * Exports print the marker as "[Photo n]" and append the picture.
 */

import type React from 'react';
import { useRef, useState } from 'react';
import { ImagePlusIcon, Loader2Icon, XIcon } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { uploadFilesToBackend } from 'components/FileManagement/hooks/fileMutations';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import {
  distinctPhotos,
  insertPhotoMarker,
  photoMarker,
  removePhotoMarker,
} from './photoMarkers';
import { useFormPhotoFiles } from './useFormPhotoFiles';

const PHOTO_TYPES = ['image/png', 'image/jpeg'];

const photoFilesFrom = (list: FileList | null | undefined): File[] =>
  Array.from(list ?? []).filter((file) => PHOTO_TYPES.includes(file.type));

const captionFor = (file: File): string => file.name.replace(/\.[^.]+$/, '');

type PhotoTextareaProps = Omit<
  React.ComponentPropsWithoutRef<'textarea'>,
  'value' | 'onChange'
> & {
  value: string;
  onValueChange: (value: string) => void;
};

export const PhotoTextarea: React.FC<PhotoTextareaProps> = ({
  value,
  onValueChange,
  disabled,
  className,
  onPaste,
  onDrop,
  onDragOver,
  ...props
}) => {
  const incidentId = useActualIncidentId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  // The upload is async; the marker must land in whatever the box holds by then.
  const latestValue = useRef(value);
  latestValue.current = value;
  const photos = distinctPhotos(value);
  const { data: files } = useFormPhotoFiles(
    incidentId,
    photos.map((photo) => photo.fileId)
  );
  const canAddPhotos = !!incidentId && !disabled;

  const addPhotos = async (picked: File[]) => {
    if (!incidentId || picked.length === 0) return;
    const element = textareaRef.current;
    const start = element?.selectionStart ?? value.length;
    const end = element?.selectionEnd ?? start;
    setUploading(true);
    try {
      const uploaded = await uploadFilesToBackend({
        files: picked,
        workspaceId: incidentId,
        ragIndex: false,
      });
      const markers = uploaded
        .map((file, index) =>
          file.id ? photoMarker(file.id, captionFor(picked[index])) : null
        )
        .filter((marker): marker is string => !!marker);
      if (markers.length === 0) throw new Error('No file id returned');
      const current = latestValue.current;
      const next = insertPhotoMarker(
        current,
        markers.join('\n'),
        Math.min(start, current.length),
        Math.min(end, current.length)
      );
      onValueChange(next.text);
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
        textareaRef.current?.setSelectionRange(next.cursor, next.cursor);
      });
    } catch (error) {
      console.error('Photo upload failed', error);
      toast.error('The photo could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    onPaste?.(event);
    const pasted = photoFilesFrom(event.clipboardData?.files);
    if (!canAddPhotos || pasted.length === 0) return;
    event.preventDefault();
    void addPhotos(pasted);
  };

  const handleDrop = (event: React.DragEvent<HTMLTextAreaElement>) => {
    onDrop?.(event);
    const dropped = photoFilesFrom(event.dataTransfer?.files);
    if (!canAddPhotos || dropped.length === 0) return;
    event.preventDefault();
    void addPhotos(dropped);
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <Textarea
        {...props}
        ref={textareaRef}
        className={className}
        disabled={disabled}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        onPaste={handlePaste}
        onDragOver={(event) => {
          onDragOver?.(event);
          if (canAddPhotos && event.dataTransfer?.types.includes('Files')) {
            event.preventDefault();
          }
        }}
        onDrop={handleDrop}
      />
      {photos.length > 0 || canAddPhotos ? (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          {photos.map((photo) => {
            const file = files?.get(photo.fileId);
            return (
              <figure
                key={photo.fileId}
                className="tw:relative tw:m-0 tw:flex tw:w-24 tw:flex-col tw:gap-1"
              >
                <div className="tw:flex tw:h-16 tw:w-24 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:bg-muted">
                  {file?.signedUrl ? (
                    <img
                      src={file.signedUrl}
                      alt={photo.caption || photo.label}
                      className="tw:h-full tw:w-full tw:object-cover"
                    />
                  ) : (
                    <span className="tw:text-xs tw:text-muted-foreground">
                      {photo.label}
                    </span>
                  )}
                </div>
                <figcaption className="tw:truncate tw:text-xs tw:text-muted-foreground">
                  {photo.label}
                  {photo.caption ? ` · ${photo.caption}` : ''}
                </figcaption>
                {!disabled ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon-xs"
                    className="tw:absolute tw:top-1 tw:right-1"
                    aria-label={`Remove ${photo.label}`}
                    onClick={() =>
                      onValueChange(removePhotoMarker(value, photo.fileId))
                    }
                  >
                    <XIcon />
                  </Button>
                ) : null}
              </figure>
            );
          })}
          {canAddPhotos ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(photos.length > 0 && 'tw:self-start')}
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
              >
                {uploading ? (
                  <Loader2Icon className="tw:animate-spin" />
                ) : (
                  <ImagePlusIcon />
                )}
                Insert photo
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept={PHOTO_TYPES.join(',')}
                multiple
                hidden
                onChange={(event) => {
                  void addPhotos(photoFilesFrom(event.target.files));
                  event.target.value = '';
                }}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default PhotoTextarea;
